import { FC, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import Button from "../../components/new/Button/Button";
import NewTopChains from "./NewTopChains";
import useFireflyChains from "../../core/apiHooks/useFireflyChains";
import Loader from "../../components/new/Loader/Loader";
import { getFormattedThousand } from "@helpers";
import PageContent from "../../containers/PageContent/PageContent";
import Page from "../../containers/Page/Page";
import NotesPopup from "./NotesPopup/NotesPopup";
import { AppContext } from "../../AppContext";
import { ChainsFilters, DEFAULT_FILTER_TITLE, iChainsFilters } from "@models/ChainsFilters";
import { useChainsExport } from "@apiHooks/useChainsExport";
import NotificationPopup from "../../components/NotificationPopup/NotificationPopup";
import UsChainsHeader from "@pages/_shared/UsChainsHeader/UsChainsHeader";
import useQueryParam from "@hooks/useQueryParam";
import useTerritoryOptions from "@apiHooks/useTerritoryOptions";
import useUrlChainsFilters, { toArray } from "./hooks/useUrlChainsFilters";
import { getEnding } from "@datassential/platform-ui-lib";
import PlacesFoodsTabs from "./PlacesFoodsTabs/PlacesFoodsTabs";
import { getOnTheMenuTerm, shouldShowOnTheMenuTabs } from "./FireflyChains.helpers";
import {
    CHAINS_TAB_PARAM,
    FOODS_TAB_PARAM_VALUE,
    parsePlacesFoodsTab,
    PlacesFoodsTab,
} from "./PlacesFoodsTabs/PlacesFoodsTabs.helpers";

function getTitle(
    showOnTheMenuTabs: boolean,
    activeTab: PlacesFoodsTab,
    count: number,
    term: string,
): ReactNode {
    if (!showOnTheMenuTabs) return <>{getFormattedThousand(count)} matching chains</>;
    if (activeTab === "foods") {
        if (!term) return <>chain menu items</>;
        return <>chain menu items featuring&nbsp;<span>{term}</span></>;
    }
    return <>{getFormattedThousand(count)}&nbsp;{getEnding('chain', count)} offering&nbsp;<span>{term}</span></>;
}

const FireflyChains: FC = () => {
    const {
        globalVariables: {
            hasFFChainsFeaturePermission: hasPermission,
        },
    } = useContext(AppContext);
    const [count, setCount] = useState(0);
    const { isLoading, data } = useFireflyChains();
    const [isNotesShown, setIsNotesShown] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState<iChainsFilters>(new ChainsFilters());
    const [selectedChainsIds, setSelectedChainsIds] = useState<number[]>([]);

    const tabParam = useQueryParam(CHAINS_TAB_PARAM);
    const [activeTab, setActiveTab] = useState<PlacesFoodsTab>(() => parsePlacesFoodsTab(tabParam));
    const metroParams = toArray(useQueryParam('metros'));
    const { states: territoryStates, metros: territoryMetros, isLoading: isTerritoryLoading } = useTerritoryOptions();
    const { initialFilters, initialLocationsTab, isReady } = useUrlChainsFilters(
        metroParams.length > 0 && !isTerritoryLoading ? territoryMetros : null,
    );

    // State/metro filter options come from GetTerritoryOptions (matches the Sales
    // workspace); other facets stay chains-derived. Keep the chains' "-" (no value)
    // option since territory has none.
    const enhancedData = useMemo(() => {
        if (isTerritoryLoading) return data;
        const withPlaceholder = (labels: string[], source: string[]) =>
            source.includes(DEFAULT_FILTER_TITLE) ? [...labels, DEFAULT_FILTER_TITLE] : labels;
        return {
            ...data,
            filtersData: new ChainsFilters({
                subModel: {
                    ...data.filtersData,
                    metros: withPlaceholder(territoryMetros.map(m => m.label), data.filtersData.metros),
                    states: withPlaceholder(territoryStates.map(s => s.label), data.filtersData.states),
                },
            }),
        };
    }, [data, territoryMetros, territoryStates, isTerritoryLoading]);

    const {
        requestExportChains,
        isDownloading,
        isNotificationShow
    } = useChainsExport(selectedChainsIds, enhancedData.filtersData, selectedFilters);

    const handleFiltersChange = useCallback((selectedFilters: iChainsFilters, selectedChainsIds: number[]) => {
        setSelectedFilters(selectedFilters);
        setSelectedChainsIds(selectedChainsIds);
    }, []);

    const handleClosePopup = useCallback(() => setIsNotesShown(false), []);

    const handleTabChange = useCallback((tab: PlacesFoodsTab) => {
        setActiveTab(tab);
        const params = new URLSearchParams(window.location.search);
        if (tab === "foods") {
            params.set(CHAINS_TAB_PARAM, FOODS_TAB_PARAM_VALUE);
        } else {
            params.delete(CHAINS_TAB_PARAM);
        }
        const query = params.toString();
        window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    }, []);

    const onTheMenuTerm = getOnTheMenuTerm(selectedFilters.includes);
    const showOnTheMenuTabs = shouldShowOnTheMenuTabs(selectedFilters.includes, activeTab);

    return (
        /* @ts-ignore */
        <Page>
            <PageContent>
                <UsChainsHeader data={data} isLoading={isLoading}/>
                <div className="NewTopChains">
                    {(isLoading || !isReady || isTerritoryLoading) && <Loader/>}
                    {!isLoading && isReady && !isTerritoryLoading && (
                        <div className="NewTopChains__wrapper">
                            <NotificationPopup
                                isOpened={isNotificationShow}
                                hasCloseButton={false}
                                style={NotificationPopupStyles}
                                className="notification-popup--page"
                            >
                                {NotificationPopupContent}
                            </NotificationPopup>
                            <div className="NewTopChains__header">
                                <div
                                    className="NewTopChains__part"
                                    data-testid={`text${activeTab === 'foods' ? 'Foods' : 'Places'}Title`}
                                >
                                    {getTitle(showOnTheMenuTabs, activeTab, count, onTheMenuTerm)}
                                </div>
                                <div className="NewTopChains__part">
                                    {showOnTheMenuTabs && (
                                        <div className="NewTopChains__tabs NewTopChains__tabs--header">
                                            <PlacesFoodsTabs
                                                activeTab={activeTab}
                                                onChange={handleTabChange}
                                            />
                                        </div>
                                    )}
                                    {hasPermission && (
                                        <Button
                                            className="NewTopChains__button"
                                            modifiers={["primary", "accent"]}
                                            onClick={requestExportChains}
                                            isLoading={isDownloading}
                                            testId="buttonPlacesExportXls"
                                        >
                                            XLS Export
                                        </Button>
                                    )}
                                    {hasPermission && (
                                        <Button
                                            className="NewTopChains__button"
                                            modifiers={["primary", "accent-gradient"]}
                                            onClick={() => setIsNotesShown(true)}
                                        >
                                            My Notes
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <NewTopChains
                                data={enhancedData}
                                setCount={setCount}
                                hasFilters={hasPermission}
                                handleFiltersChange={handleFiltersChange}
                                initialFilters={initialFilters}
                                initialLocationsTab={initialLocationsTab}
                                activeTab={activeTab}
                                tableTestId="tablePlaces"
                            />

                            {hasPermission && (
                                <NotesPopup
                                    isShown={isNotesShown}
                                    onClose={handleClosePopup}
                                />
                            )}
                        </div>
                    )}
                </div>
            </PageContent>
        </Page>
    );
};
const NotificationPopupContent = <span>Report is ready to save...</span>;
const NotificationPopupStyles = {
    width: "auto",
    height: "auto"
};

export default FireflyChains;